import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Trophy, Medal, Award, Crown, Users, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { GeminiHeader } from "@/components/GeminiHeader";
import { motion } from "framer-motion";

interface TeamXP {
  team: string;
  total_xp: number;
  member_count: number;
}

interface SafetyChampion {
  id: string;
  display_name: string;
  total_xp: number;
  verified_incidents_count: number;
  team: string;
}

const Leaderboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [teamLeaderboard, setTeamLeaderboard] = useState<TeamXP[]>([]);
  const [safetyChampions, setSafetyChampions] = useState<SafetyChampion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase.from("worker_profiles").select("*").eq("user_id", user.id).single();
    setCurrentUser(profile);

    let teamQuery = supabase
      .from("team_xp_ledger")
      .select("team, xp_amount");
    
    if (profile?.site_id) {
      teamQuery = teamQuery.eq("site_id", profile.site_id);
    } else if (profile?.company_id) {
      teamQuery = teamQuery.eq("company_id", profile.company_id);
    }

    const { data: teamXpData } = await teamQuery;

    const teamTotals: Record<string, number> = {};
    teamXpData?.forEach(entry => {
      teamTotals[entry.team] = (teamTotals[entry.team] || 0) + entry.xp_amount;
    });

    let workersQuery = supabase
      .from("worker_profiles")
      .select("team");
    
    if (profile?.site_id) {
      workersQuery = workersQuery.eq("site_id", profile.site_id);
    } else if (profile?.company_id) {
      workersQuery = workersQuery.eq("company_id", profile.company_id);
    }

    const { data: workers } = await workersQuery;
    
    const memberCounts: Record<string, number> = {};
    workers?.forEach(w => {
      if (w.team) {
        memberCounts[w.team] = (memberCounts[w.team] || 0) + 1;
      }
    });

    const teams: TeamXP[] = Object.keys(teamTotals).map(team => ({
      team,
      total_xp: teamTotals[team],
      member_count: memberCounts[team] || 0
    })).sort((a, b) => b.total_xp - a.total_xp).slice(0, 10);

    setTeamLeaderboard(teams);

    let championsQuery = supabase
      .from("worker_profiles")
      .select("id, display_name, total_xp, verified_incidents_count, team")
      .gte("verified_incidents_count", 3)
      .order("verified_incidents_count", { ascending: false })
      .limit(10);
    
    if (profile?.site_id) {
      championsQuery = championsQuery.eq("site_id", profile.site_id);
    } else if (profile?.company_id) {
      championsQuery = championsQuery.eq("company_id", profile.company_id);
    }

    const { data: champions } = await championsQuery;
    setSafetyChampions(champions || []);

    setIsLoading(false);
  };

  const getTeamRankDisplay = (rank: number) => {
    if (rank === 1) return { icon: <Crown className="h-6 w-6" />, color: "text-warning bg-warning/10" };
    if (rank === 2) return { icon: <Medal className="h-5 w-5" />, color: "text-muted-foreground bg-muted" };
    if (rank === 3) return { icon: <Award className="h-5 w-5" />, color: "text-amber-600 bg-amber-500/10" };
    return { icon: <span className="text-sm font-bold">{rank}</span>, color: "text-muted-foreground bg-muted/50" };
  };

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader 
        title={t("leaderboard.title")} 
        subtitle="Tim & Safety Champions"
      />

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Safety Champions Section */}
        {safetyChampions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-5 border border-border bg-card mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-warning" />
              <h2 className="font-bold">Safety Champions</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Pekerja dengan 3+ laporan bahaya terverifikasi bulan ini
            </p>
            <div className="flex flex-wrap gap-2">
              {safetyChampions.map((champion) => (
                <div
                  key={champion.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-warning/10 border border-warning/20"
                >
                  <Star className="h-4 w-4 text-warning" />
                  <span className="text-sm font-medium">{champion.display_name}</span>
                  <span className="text-xs text-muted-foreground">({champion.verified_incidents_count} reports)</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Team Leaderboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl p-5 border border-border bg-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="font-bold">Team Rankings</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Total XP tim dari laporan bahaya yang dikonfirmasi
          </p>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : teamLeaderboard.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Belum ada data tim. Lapor bahaya untuk mulai mengumpulkan XP tim!
            </div>
          ) : (
            <div className="space-y-2">
              {teamLeaderboard.map((team, index) => {
                const rankDisplay = getTeamRankDisplay(index + 1);
                const isCurrentTeam = currentUser?.team === team.team;
                
                return (
                  <motion.div
                    key={team.team}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index }}
                    className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                      isCurrentTeam
                        ? "bg-primary/10 border-2 border-primary"
                        : "bg-card hover:bg-muted/50 border border-border"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${rankDisplay.color}`}>
                      {rankDisplay.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{team.team}</div>
                      <div className="text-xs text-muted-foreground">{team.member_count} anggota</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-warning" />
                      <span className="font-bold">{team.total_xp || 0}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Leaderboard;